import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { getCoreSettings, updateCoreSettings } from '../../../services/CoreSettingsService';

const ELECTRONIC_BILLING_PROVIDER = 'FACTURA_LISTA';

const MP_FIELDS = [
  {
    key: 'mercadopago_user_id',
    label: 'User ID',
    disabled: true,
    placeholder: 'Se autogenera al guardar con el Access Token',
    helperText: 'No es necesario cargarlo manualmente.'
  },
  { key: 'mercadopago_application_number', label: 'Client ID' },
  { key: 'mercadopago_public_key', label: 'Public Key' },
  { key: 'mercadopago_access_token', label: 'Access Token' },
  { key: 'mercadopago_webhook_secret', label: 'Webhook Secret', type: 'password' }
];

const FACTURA_LISTA_FIELDS = [
  { key: 'factura_lista_id_emisor', label: 'ID Emisor' },
  { key: 'factura_lista_cod_sucursal', label: 'Código sucursal' },
  { key: 'factura_lista_usuario', label: 'Usuario' },
  { key: 'factura_lista_password', label: 'Password', type: 'password' },
  { key: 'factura_lista_billing_indicator', label: 'Indicador de facturación', type: 'select' },
];

const FACTURA_LISTA_BILLING_INDICATOR_OPTIONS = [
  { value: '3', label: '3 - IVA básica' },
  { value: '2', label: '2 - IVA mínima' },
  { value: '1', label: '1 - No gravado' },
  { value: '16', label: '16 - Literal E' }
];

const SUPPORT_FIELDS = [
  {
    key: 'support_notification_email',
    label: 'Email para notificaciones de soporte',
    helperText: 'Podés ingresar más de un correo separado por coma.'
  }
];

const META_FIELDS = [
  {
    key: 'meta_app_id',
    label: 'Meta App ID',
    helperText: 'App ID de la app Axium en Meta for Developers.'
  },
  {
    key: 'meta_app_secret',
    label: 'Meta App Secret',
    type: 'password',
    helperText: 'App Secret de la app Axium. No pertenece a un tenant.'
  },
  {
    key: 'marketing_integration_secret',
    label: 'Marketing Integration Secret',
    type: 'password',
    helperText: 'Clave interna para firmar OAuth y cifrar tokens. Podés generarla con: openssl rand -base64 32'
  },
  {
    key: 'meta_graph_version',
    label: 'Meta Graph Version',
    placeholder: 'v20.0',
    helperText: 'Opcional. Si queda vacío se usa v20.0.'
  }
];

const CoreConfigPage = () => {
  const [settings, setSettings] = useState({});
  const [saving, setSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      const data = await getCoreSettings();
      const mapped = {};
      data.forEach((item) => {
        mapped[item.key] = item.value;
      });
      setSettings(mapped);
    };
    load();
  }, []);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const normalizedSettings = {
        ...settings,
        mercadopago_active: 'true',
        core_electronic_billing_provider: ELECTRONIC_BILLING_PROVIDER
      };
      const payload = Object.entries(normalizedSettings).map(([key, value]) => ({
        key,
        value: value ?? ''
      }));
      await updateCoreSettings(payload);
      setSuccessOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={3}>
      <PageHeader title="Configuración" subtitle="Configuración general del sistema" />

      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Mercado Pago"
          subheader="Credenciales de la cuenta Axium para cobros."
        />
        <CardContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            En producción, el webhook de Mercado Pago es siempre:{' '}
            <strong>https://api.axium.com.uy/webhook/core/mercado_pago</strong>.
            {' '}Eventos a suscribir en MP: <strong>payment</strong> y <strong>subscription_preapproval</strong>.
            No tenemos que suscribir nada manualmente desde Axium. La <strong>Webhook Secret</strong> es opcional, pero si se carga Axium validará la firma <strong>x-signature</strong> antes de procesar la notificación.
          </Alert>

          <Grid container spacing={2}>
            {MP_FIELDS.map((field) => (
              <Grid item xs={12} md={6} key={field.key}>
                <TextField
                  fullWidth
                  type={field.type || 'text'}
                  label={field.label}
                  value={settings[field.key] || ''}
                  disabled={field.disabled}
                  placeholder={field.placeholder}
                  helperText={field.helperText}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <LoadingButton variant="contained" onClick={handleSave} loading={saving}>
              Guardar
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Meta / Instagram"
          subheader="Credenciales globales de la app Axium para conectar cuentas de Instagram por empresa."
        />
        <CardContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Estas credenciales son de Axium, no de cada empresa. Cada tenant después conecta su propia página e Instagram desde Marketing.
            El redirect URI local debe coincidir con la URL del dashboard donde se toca “Conectar con Meta”.
          </Alert>

          <Grid container spacing={2}>
            {META_FIELDS.map((field) => (
              <Grid item xs={12} md={6} key={field.key}>
                <TextField
                  fullWidth
                  type={field.type || 'text'}
                  label={field.label}
                  value={settings[field.key] || ''}
                  placeholder={field.placeholder}
                  helperText={field.helperText}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <LoadingButton variant="contained" onClick={handleSave} loading={saving}>
              Guardar
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Facturación electrónica del sistema"
          subheader="Provider fiscal para la emisión de comprobantes de Axium Core."
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                Configuración de Factura Lista para la facturación central de Axium.
              </Alert>
            </Grid>
            {FACTURA_LISTA_FIELDS.map((field) => (
              <Grid item xs={12} md={6} key={field.key}>
                {field.key === 'factura_lista_billing_indicator' ? (
                  <FormControl fullWidth>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={settings[field.key] || '3'}
                      onChange={(e) => handleChange(field.key, e.target.value)}
                    >
                      {FACTURA_LISTA_BILLING_INDICATOR_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    type={field.type || 'text'}
                    label={field.label}
                    value={settings[field.key] || ''}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                  />
                )}
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <LoadingButton variant="contained" onClick={handleSave} loading={saving}>
              Guardar
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Soporte"
          subheader="Notificaciones internas cuando empresas o cadetes escriben en soporte."
        />
        <CardContent>
          <Grid container spacing={2}>
            {SUPPORT_FIELDS.map((field) => (
              <Grid item xs={12} md={6} key={field.key}>
                <TextField
                  fullWidth
                  label={field.label}
                  value={settings[field.key] || ''}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  helperText={field.helperText}
                />
              </Grid>
            ))}
          </Grid>

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <LoadingButton variant="contained" onClick={handleSave} loading={saving}>
              Guardar
            </LoadingButton>
          </Box>
        </CardContent>
      </Card>

      <Snackbar open={successOpen} autoHideDuration={2500} onClose={() => setSuccessOpen(false)}>
        <Alert severity="success" onClose={() => setSuccessOpen(false)}>
          Configuración guardada
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CoreConfigPage;
